import { Box, Divider, FormControlLabel, Grid, IconButton, Switch, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import {
  BadgeOutlined,
  CancelOutlined,
  Inventory2Outlined,
  NotificationsOutlined,
  PersonOutlined,
  SaveOutlined,
} from "@mui/icons-material";
import CustomTextField from "../../common/CustomTextField";
import userSlice, {
  fetchAllUserRoles,
  saveUsersProfile,
  updateUserInfoObject,
  useUserReducer,
  clearUserObject,
  UserRole,
  UserNotificationPreference,
  searchUserNotificationPreference,
  saveUserNotificationPreference,
  fetchUserProfileById,
} from "../../../redux/slice/userSlice";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomPasswordTextField from "../../common/CustomPasswordTextField";
import CustomNametField from "../../common/CustomNametField";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  AgencyInfo,
  MainErrorInfo,
  SingleAlertInfo,
  fetchAgenciesList,
  fetchManufacturersList,
  setMainErrorObj,
  setSingleAlertObj,
  showHideLoadingDialog,
  useCommonReducer,
} from "../../../redux/slice/commonSlice";
import { useLocation, useNavigate } from "react-router-dom";
import React from "react";
import { PracticeInfo, getPracticesByUserRole, usePracticeSlice } from "../../../redux/slice/practiceSlice";
import CustomPatternTextField from "../../common/CustomPatternTextField";
import { sortNames } from "../../../utils/Utility";
import { selectTokenValues, updateElementInAuthSlice, useAuthSlice } from "../../../redux/slice/authSlice";
import { USER_ROLES, NOTIFICATION_TYPE_LIST, NOTIFICATION_TYPE, USER_ROLE_ENTITY_NAMES } from "../../../constants/applicationConstants";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { fetchActiveBillers, usePracticeApproveSlice } from "../../../redux/slice/practice/practiceApproveSlice";
import { CodeTable, getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import CustomMultipleDropDown from "../../common/CustomMultipleDropDown";
import { fetchActivePracticeProviders, fetchAllProviderUsedPracticeIDs, fetchPracticeProviderByUser, fetchUsedPracticeProviderIDs, usePracticeProvidersSlice } from "../../../redux/slice/practice/practiceProvidersSlice";
import { fetchPracticeProductsDataUsingDateOfServiceAndProvider, fetchPracticeProductsDataWithoutDetails, usePracticeProductsSlice } from "../../../redux/slice/practice/practiceProductSlice";
import { fetchPracticeByIdData, fetchPracticesByIdsData, usePracticeEditSlice } from "../../../redux/slice/practice/practiceEditSlice";
import CustomCheckbox from "../../common/CustomCheckBox";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import { StringLiteral } from "typescript";
import { usePracticeLocationsSlice } from "../../../redux/slice/practice/practiceLocationSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import ProfileImageUpload from "./ProfileImageUpload";
import { DocumentItemInfo } from "../../../redux/slice/product/createProductSlice";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

type Option = {
  value: string | number;
  label: string;
  entityName?: string;
};

type PracticeProviderOption = {
  label: string;
  value: string | number;
  firstName: string;
  middleName?: string;
  lastName: string;
  email?: string;
};

type providerAddressOptions = {
  label: string;
  value: string | number;
  streetAddress: string;
  unitNumber: string;
  city: string;
  state: string;
  zipCode: string;
};

export type UserCreateObj = {
  userId: string;
  email: string;
  firstName: string;
  middleName: string;
  lastName: string;
  userRoleId: number | undefined;
  phone: string;
  password: string;
  confirmPassword: string;
  entityId: number;
  entityIds: number[] | undefined;
  practiceProvider?: string | number;
  eligibleProducts?: number[];
  locationId?: number;
  passwordResetRequired: boolean;
  profileDocument?: DocumentItemInfo | null;
};

const CreateUser = () => {
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    primaryBtnSx,
    cancelBtnSx,
    inputFieldSx,
    dropdownWrapSx,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden" as const,
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.7, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}{required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
    </Typography>
  );
  // List pages (OrderList / PatientsList / UserList) keep every control at one height via filterPanelSx.
  // Apply those same Autocomplete/input locks to the whole User Details form so Practice matches siblings.
  const controlHeight = "42px";
  const controlHeightSx = {
    minHeight: `${controlHeight} !important`,
    height: `${controlHeight} !important`,
    maxHeight: `${controlHeight} !important`,
    boxSizing: "border-box !important",
    alignItems: "center",
  };
  const userDetailsFieldsSx = {
    px: 2.8,
    py: 2,
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box" as const,
    "& .MuiAutocomplete-root, & .MuiFormControl-root": {
      width: "100%",
      maxWidth: "100%",
    },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      ...controlHeightSx,
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
      padding: "0 38px 0 12px !important",
      flexWrap: "nowrap !important",
      overflow: "hidden !important",
    },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "50% !important",
      transform: "translateY(-50%)",
      right: "8px !important",
    },
    "& .MuiChip-root": {
      borderRadius: "999px",
      backgroundColor: isDarkModal ? "rgba(20,184,166,0.16)" : "rgba(13,148,136,0.12)",
      color: mTokens.accent,
      border: `1px solid ${isDarkModal ? "rgba(20,184,166,0.26)" : "rgba(13,148,136,0.25)"}`,
      fontWeight: 600,
      maxWidth: "110px",
      height: "24px !important",
      flexShrink: 1,
      minWidth: 0,
    },
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  };
  // Same wrap as modal dropdown/input styles, but keep list-page 42px height (override minHeight:unset).
  const mergeControlSx = (base: object) => {
    const nested = (base as Record<string, unknown>)["& .MuiOutlinedInput-root, & .MuiInputBase-root"];
    return {
      ...(nested && typeof nested === "object" ? nested : {}),
      ...controlHeightSx,
    };
  };
  const dropdownFieldWrapSx = (hasError = false) => {
    const base = dropdownWrapSx(hasError);
    return {
      ...base,
      width: "100%",
      "& .MuiOutlinedInput-root, & .MuiInputBase-root": mergeControlSx(base),
    };
  };
  const textFieldWrapSx = (hasError = false) => {
    const base = inputFieldSx(hasError);
    return {
      ...base,
      width: "100%",
      "& .MuiOutlinedInput-root, & .MuiInputBase-root": mergeControlSx(base),
    };
  };
  const multiDropdownWrapSx = (hasError = false) => {
    const base = dropdownFieldWrapSx(hasError);
    return {
      ...base,
      width: "100%",
      maxWidth: "100%",
      "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
        ...mergeControlSx(base),
        flexWrap: "nowrap !important",
        overflow: "hidden !important",
        width: "100%",
        maxWidth: "100%",
        padding: "0 38px 0 12px !important",
      },
      "& .MuiAutocomplete-root, & .MuiFormControl-root, & .MuiTextField-root": {
        width: "100%",
        maxWidth: "100%",
      },
      "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input": {
        minWidth: "0 !important",
        width: "0 !important",
        flexGrow: 1,
        padding: "0 !important",
      },
      "& .MuiFormHelperText-root": {
        marginLeft: 0,
        marginRight: 0,
      },
    };
  };
  const location = useLocation();
  const userData = location.state?.user;
  const provider = location.state?.providerData;
  const createProviderUserByPracticeProvider = Boolean(provider);
  const {
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    setError,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<UserCreateObj>();
  const { userRoles, userObject, userNotificationPreferenceData ,fetchAllUserRoleStatus} = useUserReducer();
  const { manufacturerInfoList, agencyInfoList } =
    useCommonReducer();
  const { practiceByUserRole } = usePracticeSlice();
  const { practiceEditInfo, multiplePracticesInfo } = usePracticeEditSlice();
  const { practiceProvidersInfo, practiceProviderInfo, allProviderUsedPracticeIds, usedPracticeProviderIds } = usePracticeProvidersSlice();
  const { practiceProductsInfo } = usePracticeProductsSlice();
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const { userInfo } = useAuthSlice();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const [switchStates, setSwitchStates] = useState<{ [key: string]: boolean }>({});
  const [eligibleProducts, setEligibleProducts] = useState<number[]>([]);
  const [providerAssigned, setProviderAssigned] = useState(false);
  const [optionPopupShow, setOptionPopupShow] = React.useState<OptionPopupObjectProps>({ message: "", show: false });
  const [selectedUserEntity, setSelectedUserEntity] = useState("");

  const [initialProfileSrc, setInitialProfileSrc] = useState<string | undefined>(undefined);
  const [file, setFile] = useState<File | null>(null);
  const [profileImageRemoved, setProfileImageRemoved] = useState(false);

  const { billerList } = usePracticeApproveSlice();
  const canManageProviderProductsSection = useSelector((state: RootState) => selectTokenValues("canManageProviderProductsSection")(state));
  //cs-442
  
  // CS-1452 - Start
  const isEditing = Boolean(userData || Boolean(userObject));

  useEffect(() => {
    if (!userObject) {
      if (userData?.userId) {
        dispatch(fetchUserProfileById(userData?.userId));
      }
    }
  }, [userData, userObject, dispatch]);
  // CS-1452 - End

  const notificationOptions = useMemo(() => {
    let notificationTypes: CodeTable[] = [];
    if (allCodeTablesValuesMap) {
      notificationTypes = getCodeTableValuesByTableName(allCodeTablesValuesMap, NOTIFICATION_TYPE_LIST);
    }
    let options: { label: string, value: any }[] = [];
    notificationTypes?.map((option) => {
      options.push({ label: option.displayValue, value: option.displayValue });
    });
    return options;
  }, [allCodeTablesValuesMap]);

   //Calling these again when notfication preference is set true
   useEffect(() => {
    if (fetchAllUserRoleStatus === "success") {
      const data: UserNotificationPreference = {
        userId: userData?.userId,
        notificationType: NOTIFICATION_TYPE,
        notificationSubType: null
      }
      dispatch(searchUserNotificationPreference(data))
    }

  }, [fetchAllUserRoleStatus])


  useEffect(() => {
    if(userObject?.canAccessProviderProducts){
      setEligibleProducts(userObject?.canAccessProviderProducts);
    }
  }, [userObject])


  //handling the toggle switch
  useEffect(() => {
    // Initialize state based on userNotificationPreferenceData
    if (Array.isArray(userNotificationPreferenceData)) {
      const initialStates: { [key: string]: boolean } = {};
      userNotificationPreferenceData.forEach((pref) => {
        initialStates[pref.notificationSubType] = pref.isEnabled;
      });
      setSwitchStates(initialStates);
    }
  }, [userNotificationPreferenceData]);
  //
  useEffect(() => {
    const data: UserNotificationPreference = {
      userId: userData?.userId,
      notificationType: NOTIFICATION_TYPE,
      notificationSubType: null
    }
    dispatch(searchUserNotificationPreference(data))
  }, [])
  const handleSwitchToggle = async (data: { value: string }, newValue: boolean) => {

    const preference = userNotificationPreferenceData?.find((pref: { notificationSubType: string; }) =>
      pref.notificationSubType === data.value
  );

    const preferenceSelection = {
      userNotificationPreferenceId: preference ? preference.userNotificationPreferenceId : null,
      userId: userData?.userId,
      notificationType: NOTIFICATION_TYPE,
      notificationSubType: data.value,
      isEnabled: newValue,
    };

    // Call the API to update/save the preference
    await dispatch(saveUserNotificationPreference(preferenceSelection));

    // Update the local state
    setSwitchStates((prevStates) => ({
      ...prevStates,
      [data.value]: newValue,
    }));
  };
  //

  const handleCheckingForProducts = async (data: { value: string }, newValue: boolean) => {

    if (newValue) {
      setEligibleProducts((prevEligible) => [...prevEligible, parseInt(data.value)]);
    } else {
      setEligibleProducts((prevEligible) =>
        prevEligible.filter((productId) => productId !== parseInt(data.value))
      );
    }
  };

  const canManageUserProfile = useSelector((state: RootState) => selectTokenValues("canManageUsers")(state));

  // CS-1452 - Start
  useEffect(() => {
    const userId = userObject?.userId || userData?.userId;

    if (isEditing && userId) {
      dispatch(fetchPracticeProviderByUser(userId));
    }
  }, [userObject, userData, isEditing]);


  useEffect(() => {
    if (practiceProviderInfo?.practiceId) {
      dispatch(fetchPracticeByIdData(String(practiceProviderInfo?.practiceId)))
    }
  }, [practiceProviderInfo]);
  // CS-1452 - End

  // useEffect(()=>{
  //   if(isEditing){
  //   if (practiceEditInfo && practiceProviderInfo?.practiceId) {
  //     if (practiceEditInfo.billingType === "GROUP") {
  //       dispatch(fetchPracticeProductsDataWithoutDetails(String(practiceProviderInfo.practiceId)));
  //     } else {
  //       dispatch(
  //         fetchPracticeProductsDataUsingDateOfServiceAndProvider({
  //           practiceId: Number(practiceProviderInfo.practiceId),
  //           providerId: Number(practiceProviderInfo.providerId),
  //           dateOfService: "", 
  //         })
  //       );
  //     }
  //   }
  // }
  // },[practiceEditInfo, practiceProviderInfo]);


  useEffect(()=>{
    if(selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER) {
      dispatch(fetchAllProviderUsedPracticeIDs());
      dispatch(fetchUsedPracticeProviderIDs());
    }
  },[selectedUserEntity]);

  const defaultValues = useCallback(() => {
    if (userObject) {
      if (userObject.userRoleId) {
        const selectedUser = userRoleList.find((user) => user.value === userObject.userRoleId);
        if (selectedUser && selectedUser.entityName) {
          setSelectedUserEntity(selectedUser.entityName);
        }
      }
      reset(userObject);
      if (isEditing && selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER && practiceProviderInfo) {
        const providers = Array.isArray(practiceProviderInfo)
          ? practiceProviderInfo
          : [practiceProviderInfo];

        if (providers.length > 0) {
          const practiceIds = providers.map(provider => Number(provider.practiceId));
          setValue("entityIds", practiceIds);
          setValue("practiceProvider", providers[0].providerId);
          setValue("locationId", providers[0].locationId);
        }
      }
      
      // CS-1609 :Check if biller is inactive and clear entityId if not in active list
      if (isEditing && selectedUserEntity === USER_ROLE_ENTITY_NAMES.BILLER && userObject.entityId) {
        const billerExists = billerUsersList.some(biller => biller.value === userObject.entityId);
        if (!billerExists) {
          setValue("entityId", 0);
        }
      }
      // CS-1609 :Check if agency is inactive and clear entityId if not in active list
      if (isEditing && selectedUserEntity === USER_ROLE_ENTITY_NAMES.AGENCY && userObject.entityId) {
        const agencyExists = agencyList.some(agency => agency.value === userObject.entityId);
        if (!agencyExists) {
          setValue("entityId", 0);
        }
      }
      
    } else {
      reset({
        userRoleId: undefined,
        entityId: 0,
        entityIds: [],
        email: '',
        locationId: undefined,
        password: '',
        confirmPassword: '',
        phone: '',
      });
    }
  }, [userObject, reset, practiceProviderInfo]); 
  
  const convertBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const stripDataUrlPrefix = (dataUrl: string) =>
    dataUrl.replace(/^data:[^;]+;base64,/, "");

  const onSubmit = async (data: UserCreateObj) => {
    //CS-1609 Validate Biller selection if user type is Biller
    if (selectedUserEntity === USER_ROLE_ENTITY_NAMES.BILLER) {
      const billerExists = billerUsersList.some((biller:any) => biller.value === data.entityId);
      if (!billerExists || !data.entityId || data.entityId === 0) {
        const alertObj: SingleAlertInfo = {
          message: "Please select a biller.",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
        return;
      }
    }
    //CS-1609 Validate Agency selection if user type is Agency
    if (selectedUserEntity === USER_ROLE_ENTITY_NAMES.AGENCY) {
      const agencyExists = agencyList.some((agency:any) => agency.value === data.entityId);
      if (!agencyExists || !data.entityId || data.entityId === 0) {
        const alertObj: SingleAlertInfo = {
          message: "Please select an agency.",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
        return;
      }
    }
    //CS-1609 Validate Practice selection if user type is Practice or Provider
    if (selectedUserEntity === USER_ROLE_ENTITY_NAMES.PRACTICE) {
      
      // Check if entityIds array exists and has values
      if (!data.entityIds || data.entityIds.length === 0) {
        const alertObj: SingleAlertInfo = {
          message: "Please select at least one practice.",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
        return;
      }
     }
    
    let profileDocument: DocumentItemInfo | null = null;
    
    const doc = (userObject as any)?.profileDocument;
    const email = userInfo.userName || "";
    const prefix = email.includes("@") ? email.split("@")[0] : email;

    if (file) {
       const extension = file.name.split(".").pop() || "png";

      if(!profileImageRemoved){
        const base64File = await convertBase64(file);
        profileDocument = {
          documentId: doc?.documentId || 0,
          documentName: `${prefix}.${extension}`,
          type: "PROFILE",
          fileType: file.type,
          content: stripDataUrlPrefix(base64File),
        };
      }else{
        profileDocument = null;
      }
    }

    if (isEditing) {
      dispatch(updateUserInfoObject({ ...data, id: userData.userId}));
      if(selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER && data.practiceProvider) {
        data = { ...data, entityIds: [data.practiceProvider ? Number(data.practiceProvider) : 0]}
      }
      await handleSaveUserProfile({ ...data, eligibleProducts: eligibleProducts, passwordResetRequired: false, profileDocument: profileDocument});
      setEligibleProducts([]);
      // navigate(-1);
    } else {
      if (selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER) {

        const selectedPractice = multiplePracticesInfo?.find(practice =>
          practice.practiceProviders.some(provider => 
            provider.providerId === Number(selectedProviderId)
          )
        );

        if(selectedPractice?.billingType === "GROUP" && practiceProductsInfo && practiceProductsInfo?.length > 0 && eligibleProducts.length===0){
          const alertObj: SingleAlertInfo = {
            message:  "Choose at least one product to proceed",
            alertType: "error",
          };

          dispatch(setSingleAlertObj(alertObj));

        }else{
        await handleSaveUserProfile({
          ...data,
          password: data.password.replace(/\s+/g, ""),
          confirmPassword: data.confirmPassword.replace(/\s+/g, ""),
          entityIds: data.practiceProvider ? [Number(data.practiceProvider)] : [],
          eligibleProducts: eligibleProducts,
          passwordResetRequired: true,
          profileDocument: profileDocument
        });
      }
      } else {
        await handleSaveUserProfile({
          ...data,
          password: data.password.replace(/\s+/g, ""),
          confirmPassword: data.confirmPassword.replace(/\s+/g, ""),
          eligibleProducts: [],
          passwordResetRequired: true,
          profileDocument: profileDocument
        });
      }
    }
    
    if (isEditing && String(userData?.userId) === String(userInfo.userId)) {
      dispatch(updateElementInAuthSlice({
        elementName: "userInfo",
        value: {
          ...userInfo,
          profileDocument: profileDocument ?? null,
        },
      }));
    }

  };

  const onClose = () => {
    navigate(-1);
  }

  const confirmOptionPopup = () => {
    setOptionPopupShow({ message: "", show: false });
    setValue("practiceProvider", "");
    setProviderAssigned(false);
  };

  const userRoleList = useMemo(() => {
    let allUserRoles: Option[] = [];
    let availableUserRoles: UserRole[] | undefined = undefined;
    if (canManageUserProfile && canManageUserProfile.length > 0) {
      availableUserRoles = userRoles?.filter((role: UserRole) => canManageUserProfile.includes(role.roleName));
    }
    availableUserRoles?.forEach((option: UserRole) => {
      allUserRoles.push({ label: option.roleDisplayValue, value: option.userRoleId, entityName: option.entityName });
    });
    return allUserRoles;
  }, [canManageUserProfile, userRoles]);

  const manufacturersList = useMemo(() => {
    let options: Option[] = [];
    manufacturerInfoList?.map((option) => {
      options.push({
        label: option.manufacturerName,
        value: option.manufacturerId,
      });
    });
    return options;
  }, [manufacturerInfoList]);

  const agencyList = useMemo(() => {
    let options: Option[] = [];

    if (agencyInfoList && Array.isArray(agencyInfoList)) {
      const filteredAgencyList = agencyInfoList.filter((agency: AgencyInfo) =>
        agency.commissionType.trim().toUpperCase() !== "V3"
      );

      const sortedAgencyList = sortNames(filteredAgencyList, 'agencyName');

      sortedAgencyList.forEach((agency: AgencyInfo) => {
        options.push({ label: agency.agencyName, value: agency.agencyId });
      });
    }
    return options;
  }, [agencyInfoList]);

  const billerUsersList = useMemo(() => {
    let options: Option[] = [];
    billerList?.map((option) => {
      options.push({ label: option.billerName, value: option.billerId });
    });
    return options;
  }, [billerList]);

  const practiceList = useMemo(() => {
    let options: Option[] = [];
    if (practiceByUserRole) {
      if (practiceByUserRole && practiceByUserRole.length === 1) {
        setValue('entityId', parseInt(practiceByUserRole[0]?.practiceId));
        practiceByUserRole?.map(practice => {
          options.push({ value: practice.practiceId, label: practice.practiceName });
        });
      } else {
        const sortedPracticeInfo: PracticeInfo[] = sortNames(practiceByUserRole, 'practiceName');
        sortedPracticeInfo?.map(practice => {
          options.push({ value: practice.practiceId, label: practice.practiceName });
        });
      }
    }

    return options;
  }, [practiceByUserRole]);

  const practiceProvidersOptions = useMemo(() => {
    let options: PracticeProviderOption[] = [];
    practiceProvidersInfo?.map((option: any) => {
      options.push({
        label: `${option.firstName ? option.firstName : ""} ${
          option.middleName ? option.middleName + " " : ""
        }${option.lastName ? option.lastName : ""}`,
        value: option.providerId,
        firstName: option.firstName,
        middleName: option.middleName,
        lastName: option.lastName,
        email: option.email,
      });
    });

    return options;
  }, [practiceProvidersInfo]);

  // const providerAddressOptions = useMemo(() => {
  //   const practiceLocationsInfo = practiceEditInfo?.practiceLocations || [];
  //   const CurrentDate = new Date();

  //   let options: providerAddressOptions[] = [];
  //   console.log("practiceLocationsInfo", practiceLocationsInfo);
  //   practiceLocationsInfo?.filter((option: any) => (new Date(option?.inactiveDate) > CurrentDate || !option?.inactiveDate)).map((option: any) => {
  //     options.push({
  //       label: `${option.streetAddress ? option.streetAddress + ", " : ""} ${
  //         option.unitNumber ? option.unitNumber + ", " : ""
  //       }${option.city ? option.city + ", " : ""}${option.state ? option.state + ", " : ""}${option.zipCode ? option.zipCode : ""}`,
  //       value: option.locationId,
  //       streetAddress: option.streetAddress,
  //       unitNumber: option.unitNumber,
  //       city: option.city,
  //       state: option.state,
  //       zipCode: option.zipCode
  //     });
  //   });

  //   return options;
  // }, [practiceEditInfo]);

  const providerAddressOptions = useMemo(() => {
    const CurrentDate = new Date();
    const processLocations = (locations: any[]) => {
      return locations
        .filter((option) => !option?.inactiveDate || new Date(option.inactiveDate) > CurrentDate)
        .map((option) => ({
          label: `${option.streetAddress ? option.streetAddress + ", " : ""}${
            option.unitNumber ? option.unitNumber + ", " : ""
          }${option.city ? option.city + ", " : ""}${option.state ? option.state + ", " : ""}${
            option.zipCode ? option.zipCode : ""
          }`,
          value: option.locationId,
          streetAddress: option.streetAddress,
          unitNumber: option.unitNumber,
          city: option.city,
          state: option.state,
          zipCode: option.zipCode,
        }));
    };
  
    let options: providerAddressOptions[] = [];

    const practices = multiplePracticesInfo || [];
    practices.forEach((practice) => {
      const practiceLocationsInfo = practice.practiceLocations || [];
      options = options.concat(processLocations(practiceLocationsInfo));
    });
  
    return options;
  }, [practiceEditInfo, multiplePracticesInfo]);
  
  
  const handleSaveUserProfile = async (data: UserCreateObj) => {
    let errorObj: MainErrorInfo = {
      title: "",
    };

    try {
      const response = await dispatch(saveUsersProfile(data));
      if (response.payload && response.payload.message === 'SUCCESS') {
        navigate("/user_list");

        let alertMessage = isEditing ?
          "User Profile Updated Successfully!" :
          "User Profile Created Successfully!";

        const alertObj: SingleAlertInfo = {
          message: alertMessage,
          alertType: "success",
        };

        dispatch(setSingleAlertObj(alertObj));
      } else {
        let errorSentence = '';
        if (response.payload.errors.length <= 0) {
          errorSentence = response.payload.message || "An unknown error occurred.";
        } else {
          response.payload.errors.forEach((error: any, index: any) => {
            if (error.field) {
              errorSentence += `${error.field}: ${error.errorMessage}\n`;
              if (error.field === 'email') {
                setError('email', {
                  type: 'manual',
                  message: error.errorMessage,
                });
              }
            } else {
              errorSentence += `${error.reason}\n`;
            }
          });
        }

        errorObj.title = "User Profile Creation Failed!";
        errorObj.errorMessageStream = errorSentence;
        dispatch(setMainErrorObj(errorObj));
      }
    } catch (error) {
      console.log("Error creating user profile:", error);
      errorObj.title = "Error creating user profile";
      errorObj.errorMessageStream = "" + error;
      dispatch(setMainErrorObj(errorObj));
    }
  };

  const getAllList = async () => {
    try {
      dispatch(showHideLoadingDialog(true));
      const [
        resManufacturerCall,
        resAgenciesCall,
        resPracticesCall,
        resUserCall,
        resBillersCall
      ] = await Promise.all([
        dispatch(fetchManufacturersList()),
        dispatch(fetchAgenciesList()),
        dispatch(getPracticesByUserRole(true)),
        dispatch(fetchAllUserRoles()),
        dispatch(fetchActiveBillers())
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      dispatch(showHideLoadingDialog(false));
    }
  };

  useEffect(() => {
    const practiceEntityIds = getValues('entityIds') || [];
    if (practiceEntityIds !== null && practiceEntityIds !== undefined && selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER) {
      const data = { practiceIds: practiceEntityIds, onlyActiveRecords: true }
      dispatch(fetchActivePracticeProviders({ data: data, skipLoader: false }));
    }
  }, [watch('entityIds'), selectedUserEntity]);
  
  useEffect(() => {
    getAllList();
  }, [dispatch]);

  useEffect(() => {
    defaultValues();
  }, [defaultValues]);

  useEffect(() => {
    return () => {
      dispatch(clearUserObject());
    };
  }, [dispatch]);

  const selectedProviderId = watch("practiceProvider");

  useEffect(() => {
    if (typeof selectedProviderId === "string" || typeof selectedProviderId === "number") {
      const selectedProvider = practiceProvidersOptions.find(
        (option) => option.value === selectedProviderId
      );

      if(!isEditing && selectedProvider && usedPracticeProviderIds) {
        if(usedPracticeProviderIds.includes(Number(selectedProviderId))){
          setProviderAssigned(true);
          setOptionPopupShow({
            message: `The selected provider already assigned to a user profile `,
            show: true
          });
        } else {
          setProviderAssigned(false);
        } 
      } 
  
      if (selectedProvider) {
        setValue("firstName", selectedProvider?.firstName || "");
        setValue("middleName", selectedProvider.middleName || "");
        setValue("lastName", selectedProvider.lastName || "");
        
        if (selectedProvider.email) {
          setValue("email", selectedProvider.email);
        } else {
          setValue("email", userObject?.email || "");
        }
      
      }  else if (userObject) {
        setValue("firstName", userObject?.firstName || "");
        setValue("middleName", userObject.middleName || "");
        setValue("lastName", userObject.lastName || "");
        setValue("email", userObject.email || "");
      }
      else if (selectedProvider === undefined || selectedProvider === null) {
        setValue("firstName", "");
        setValue("middleName", "");
        setValue("lastName", "");
        setValue("email", "");
      }
    }
  }, [selectedProviderId, practiceProvidersOptions, setValue, usedPracticeProviderIds]);


  useEffect(() => {
    if (isEditing && selectedProviderId && multiplePracticesInfo?.length) {
      const selectedPractice = multiplePracticesInfo.find(practice =>
        practice.practiceProviders.some(provider => 
          provider.providerId === Number(selectedProviderId)
        )
      );

      if (selectedPractice) {
      const { practiceId, billingType } = selectedPractice;

      if(billingType==="INDIVIDUAL"){
      dispatch(fetchPracticeProductsDataUsingDateOfServiceAndProvider({practiceId: Number(practiceId), dateOfService:"", providerId: Number(selectedProviderId)}));
      }else{
        dispatch(fetchPracticeProductsDataWithoutDetails(practiceId));
      }
    }
    }
  }, [dispatch, isEditing, multiplePracticesInfo, selectedProviderId]);
  
  useEffect(() => {
    const practiceEntityIds = getValues('entityIds') || [];
    
    if (!isEditing && practiceEntityIds.length > 0) {
      // dispatch(fetchPracticeByIdData(String(practiceEntityIds[0])));
      dispatch(fetchPracticesByIdsData(practiceEntityIds));
    } else if(isEditing && practiceEntityIds.length > 0) {
      dispatch(fetchPracticesByIdsData(practiceEntityIds));
    }
  }, [getValues('entityIds')]);

  useEffect(() => {
    if(createProviderUserByPracticeProvider){
      setValue("userRoleId", 11);
      setValue("practiceProvider", provider.providerId);
      setValue("entityIds", [provider.entityIds]);

      const selectedUser = userRoleList.find((user) => user.value === 11);
      if (selectedUser && selectedUser.entityName) {
        setSelectedUserEntity(selectedUser.entityName);
      }
    }
  }, [createProviderUserByPracticeProvider, provider, setValue, userRoleList]);

  const handleFieldChange = (event: any) => {
    setValue('entityId', 0);
    setValue('entityIds', []);
    setSelectedUserEntity(event?.entityName);
  };

  useEffect(() => {
    const doc = (userObject as any)?.profileDocument;
    if (!doc) {
      setInitialProfileSrc(undefined);
      return;
    }
    if (doc.content && doc.fileType) {
      setInitialProfileSrc(`data:${doc.fileType};base64,${doc.content}`);
      return;
    }
    setInitialProfileSrc(undefined);
  }, [userObject?.profileDocument]);

  useEffect(() => {
    if (file) setProfileImageRemoved(false);
  }, [file]);

  return (
    <Box sx={{ mx: { xs: "10px", sm: "20px" }, mt: "12px", pb: "72px" }}>
      <Box mb={2} display="flex" alignItems="flex-start" gap={1.5}>
        <PageBackButton tooltip={null} onClick={() => onClose()} />
        <Box data-testid="header">
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
            Users / Profile
          </Typography>
          <Typography component="h1" sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
            User Profile Management
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
            {isEditing ? "Update an existing user profile." : "Create a new user profile."}
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.4fr) minmax(0, 1fr)" }, gap: 2, alignItems: "flex-start" }}>
          <Box sx={{ ...cardSx, order: { xs: 2, md: 1 }, minWidth: 0 }} data-testid="card">
            <SectionHeader icon={<BadgeOutlined fontSize="small" />} title="User Details" />
            <Box sx={userDetailsFieldsSx}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FieldLabel label="User Type" required />
                    <Box sx={{ ...dropdownFieldWrapSx(!!errors.userRoleId) }}>
                      <CustomDropdownField
                        name="userRoleId"
                        control={control}
                        options={userRoleList}
                        label=""
                        size="small"
                        rules={{
                          required: "User Type is required.",
                          validate: {
                            notEmpty: (value: string | number) => {
                              if (typeof value === "string") {
                                return "User type is required.";
                              }
                              return value !== 0 || "User type is required.";
                            },
                          },
                        }}
                        onChangeSelect={handleFieldChange}
                        disabled= {isEditing && selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER}
                      />
                    </Box>
                  </Grid>

                  {selectedUserEntity === USER_ROLE_ENTITY_NAMES.AGENCY ||
                    selectedUserEntity === USER_ROLE_ENTITY_NAMES.ACCOUNTEXECUTIVE ? (
                    <Grid item xs={12} md={4}>
                      <FieldLabel label="Agency" required />
                      <Box sx={{ ...dropdownFieldWrapSx(!!errors.entityId) }}>
                        <CustomDropdownField
                          name="entityId"
                          control={control}
                          options={agencyList}
                          label=""
                          size="small"
                          rules={{
                            required: "Agency is required.", //CS-1609
                            validate: {
                              notZero: (value: number) =>
                                value !== 0 || "Agency is required.",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  ) : selectedUserEntity === USER_ROLE_ENTITY_NAMES.MANUFACTURER ? (
                    <Grid item xs={12} md={4}>
                      <FieldLabel label="Manufacturer" required />
                      <Box sx={{ ...dropdownFieldWrapSx(!!errors.entityId) }}>
                        <CustomDropdownField
                          name="entityId"
                          control={control}
                          options={manufacturersList}
                          label=""
                          size="small"
                          rules={{
                            required: "Manufacturer is required.", //CS-1609
                            validate: {
                              notZero: (value: number) =>
                                value !== 0 || "Manufacturer is required.",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  ) : selectedUserEntity === USER_ROLE_ENTITY_NAMES.PRACTICE ||
                  selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER ? (
                    <Grid item xs={12} md={4}>
                      <FieldLabel label="Practice" required={selectedUserEntity !== USER_ROLE_ENTITY_NAMES.PROVIDER} />
                      <Box sx={multiDropdownWrapSx(!!errors.entityIds)}>
                        {/* <CustomDropdownField */}
                        <CustomMultipleDropDown
                          name="entityIds"
                          control={control}
                          options={practiceList}
                          label=""
                          size="small"
                          rules={{
                            required: selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER 
                              ? false 
                              :"Practice is required.", //CS-1609 - Required only for Practice user type, not for Provider
                          }}
                          disabled= {isEditing && selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER}
                        />
                      </Box>
                    </Grid>
                  ) : selectedUserEntity === USER_ROLE_ENTITY_NAMES.BILLER ? (
                    <Grid item xs={12} md={4}>
                      <FieldLabel label="Biller" required />
                      <Box sx={{ ...dropdownFieldWrapSx(!!errors.entityId) }}>
                        <CustomDropdownField
                          name="entityId"
                          control={control}
                          options={billerUsersList}
                          label=""
                          size="small"
                          rules={{
                            required: "Biller is required.",
                            validate: {
                              notZero: (value: number) =>
                                value !== 0 || "Biller is required.",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  ) : (
                    <Grid item xs={12} md={4} />
                  )}
                  {selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER && (
                    <Grid item xs={12} md={4}>
                      <FieldLabel label="Practice Provider" />
                      <Box sx={{ ...dropdownFieldWrapSx(!!errors.practiceProvider) }}>
                        <CustomDropdownField
                          name="practiceProvider"
                          control={control}
                          options={practiceProvidersOptions}
                          label=""
                          size="small"
                          disabled= {isEditing && selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER}
                        />
                      </Box>
                    </Grid>)}
                    {selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER && (
                    <Grid item xs={12} md={4}>
                      <FieldLabel label="Provider Address" />
                      <Box sx={{ ...dropdownFieldWrapSx(!!errors.locationId) }}>
                        <CustomDropdownField
                          name="locationId"
                          control={control}
                          options={providerAddressOptions}
                          label=""
                          size="small"
                        />
                      </Box>
                    </Grid>)}

                  <Grid item xs={12} md={12}>
                    <Divider
                      orientation="horizontal"
                      variant="middle"
                      flexItem
                      sx={{ padding: 0, margin: 0, mt: 1, mb: 1 }}
                    />
                  </Grid>

                  <OptionsPopup
                    open={optionPopupShow.show}
                    onClose={() => setOptionPopupShow({ message: "", show: false })}
                    variant="confirm"
                    message={<>{optionPopupShow.message}</>}
                    title={optionPopupShow.title}
                    buttons={[
                      { name: "OK", color: "primary", onClick: confirmOptionPopup },
                      // { name: "Cancel", color: "secondary", onClick: cancelOptionPopup },
                    ]}
                  />

                  {/* <Grid item md={4} xs={12}>
                    <CustomTextField
                      control={control}
                      controllerName="userName"
                      label="Username"
                      rules={{ required: "Username is required." }}
                    />
                  </Grid> */}

                {!providerAssigned && (
                  <>
                    <Grid item xs={12} md={4}>
                      <FieldLabel label="Email" required />
                      <Box sx={{ ...textFieldWrapSx(!!errors.email) }}>
                        <CustomTextField
                          control={control}
                          controllerName="email"
                          label=""
                          size="small"
                          rules={{
                            required: "Email is required.",
                            pattern: {
                              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
                              message: "Invalid email.",
                            },
                          }} />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={8} />
                  </>
                )}

                  {!providerAssigned && !isEditing && (
                    <>
                      <Grid item xs={12} md={4}>
                        <FieldLabel label="Password" required />
                        <Box sx={{ ...textFieldWrapSx(!!errors.password) }}>
                          <CustomPasswordTextField
                            control={control}
                            controllerName="password"
                            label=""
                            size="small"
                            autoComplete="new-password"
                            rules={
                              isEditing ? {} : {
                                required: "Password is required.",
                                minLength: {
                                  value: 12,
                                  message: "Password should be at least 12 characters.",
                                },
                                pattern: {
                                  value: /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])(?!.*\s)/,
                                  message: "Password must contain at least one uppercase letter, one number, and one special character with no spaces"
                                },
                              }
                            }
                            isDisabled={isEditing}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FieldLabel label="Confirm Password" required />
                        <Box sx={{ ...textFieldWrapSx(!!errors.confirmPassword) }}>
                          <CustomPasswordTextField
                            control={control}
                            controllerName="confirmPassword"
                            label=""
                            size="small"
                            autoComplete="new-password"
                            rules={
                              isEditing
                                ? {}
                                : {
                                  required: "Confirmation of password is required.",
                                  validate: (value: string) =>
                                    value === watch("password") ||
                                    "Passwords do not match",
                                }
                            }
                            isDisabled={isEditing}
                          />
                        </Box>
                      </Grid>
                    </>
                  )}

                  {!providerAssigned && <>
                  <Grid item xs={12} md={12}>
                    <Divider
                      orientation="horizontal"
                      variant="middle"
                      flexItem
                      sx={{ padding: 0, margin: 0, mt: 1, mb: 1 }}
                    />
                  </Grid>

                  <Grid item md={12} xs={12}>
                    <FieldLabel label="Name" required />
                    <Box sx={inputFieldSx(!!(errors.firstName || errors.lastName))}>
                      <CustomNametField
                        control={control}
                        name=""
                        isRequired={true}
                        context=""
                        size="small"
                        usePlaceholder={true}
                        allowSpecialCharacters={true} //CS-1613
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={12}>
                    <Divider
                      orientation="horizontal"
                      variant="middle"
                      flexItem
                      sx={{ padding: 0, margin: 0, mt: 1, mb: 1 }}
                    />
                  </Grid>

                  <Grid item md={4} xs={12}>
                    <FieldLabel label="Phone" required />
                    <Box sx={textFieldWrapSx(!!errors.phone)}>
                      <CustomPatternTextField
                        control={control}
                        name="phone"
                        label=""
                        size="small"
                        rules={{ 
                          required: "Phone is required.",
                          pattern: {
                            value: /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
                            message: "Enter valid Phone Number."
                          }
                        }}
                      />
                    </Box>
                  </Grid></>}


                </Grid>
            </Box>
          </Box>

          <Box sx={{ ...cardSx, order: { xs: 1, md: 2 }, minWidth: 0 }}>
            <SectionHeader icon={<PersonOutlined fontSize="small" />} title="Profile Image" />
            <Box sx={{ px: 2.8, py: 2, display: "flex", justifyContent: "center" }}>
              <ProfileImageUpload
                value={file}
                onChange={setFile}
                maxSizeMB={5}
                title="Profile Image"
                outputType="image/png" initialSrc={initialProfileSrc}
                onRemove={() => {
                  setProfileImageRemoved(true);
                  setFile(null);
                  setInitialProfileSrc(undefined);
                }}/>
            </Box>
          </Box>
        </Box>

        {/* CS-442 */}
        {userData && userData?.userId && (
          <Box sx={{ ...cardSx, mt: 2 }}>
            <SectionHeader icon={<NotificationsOutlined fontSize="small" />} title="Notification Preference(s)" />
            <Box sx={{ px: 2.8, py: 1.5 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                  gap: 1.2,
                }}
              >
                {notificationOptions.map((data: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1.4,
                      py: 1,
                      borderRadius: "12px",
                      border: `1px solid ${mTokens.border}`,
                      backgroundColor: mTokens.headerFooterBg,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: mTokens.textPrimary }}>
                      {data.value === "TISSUETRACKING"
                        ? "TISSUE TRACKING"
                        : data.value === "PRODUCTRETURN"
                          ? "PRODUCT RETURN"
                          : data.value}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          color="success"
                          checked={switchStates[data.value] || false}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            handleSwitchToggle(data, newValue);
                          }}
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
        {(isEditing && selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER) || (selectedUserEntity === USER_ROLE_ENTITY_NAMES.PROVIDER && selectedProviderId) ? (
          practiceProductsInfo && practiceProductsInfo?.length > 0 && (
            <Box sx={{ ...cardSx, mt: 2 }}>
              <SectionHeader icon={<Inventory2Outlined fontSize="small" />} title="Products" />
              <Box sx={{ px: 2.8, py: 1.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                    gap: 1.2,
                  }}
                >
                  {practiceProductsInfo?.map((product: any, index: number) => (
                    <Box
                      key={`product-${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        px: 1.4,
                        py: 1,
                        borderRadius: "12px",
                        border: `1px solid ${mTokens.border}`,
                        backgroundColor: mTokens.headerFooterBg,
                      }}
                    >
                      <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: mTokens.textPrimary }}>
                        {product?.productName}
                      </Typography>
                      <CustomCheckbox
                        control={control}
                        checked={
                          (() => {
                            const selectedPractice = multiplePracticesInfo?.find(practice =>
                                practice.practiceProviders.some(provider =>
                                  provider.providerId === Number(selectedProviderId)
                                )
                              );
                            return selectedPractice && selectedPractice.billingType === "GROUP"
                              ? eligibleProducts.includes(product.productId)
                              : true;
                          })()
                        }
                        isDisabled={
                          (() => {
                            const selectedPractice = multiplePracticesInfo?.find(practice =>
                                practice.practiceProviders.some(provider =>
                                  provider.providerId === Number(selectedProviderId)
                                )
                              );

                            return canManageProviderProductsSection && selectedPractice && selectedPractice.billingType === "GROUP" ? false : true;
                          })()
                        }
                        onChange={(e) =>
                          handleCheckingForProducts({ value: product.productId.toString() }, e.target.checked)
                        }
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )
        ) : null}
        {/* CS-1426 - Start */}
        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1.2} justifyContent="flex-end" mt={2}>
          {!providerAssigned && (
            <Button
              type="submit"
              removeUpperCase
              startIcon={<SaveOutlined />}
              sx={{
                ...primaryBtnSx,
                minWidth: { sm: 180 },
                backgroundColor: mTokens.accent,
                boxShadow: "0 4px 18px rgba(20,184,166,.35)",
                "& .MuiTypography-root": { color: "#ffffff !important" },
                "& .MuiButton-startIcon": { color: "#ffffff" },
              }}
            >
              {isEditing ? "Update User" : "Create User"}
            </Button>
          )}
          <Button
            variant="outlined"
            removeUpperCase
            color="error"
            startIcon={<CancelOutlined />}
            onClick={() => onClose()}
            sx={{
              ...cancelBtnSx,
              minWidth: { sm: 140 },
              "& .MuiTypography-root": { color: `${mTokens.error} !important` },
              "& .MuiButton-startIcon": { color: mTokens.error },
            }}
          >
            Cancel
          </Button>
        </Box>
        {/* CS-1426 - End */}
      </form>
    </Box>
  );
};

export default CreateUser;